import { Button, LayoutGrid, Select, SelectProps } from '@archer-technologies/ui-components';
import * as React from 'react';
import { isFilterValueEmpty } from './helpers';
import { ColumnFilterProps, UseFilterComponentRefs } from './types';

const useCheckColumnFilterRefs: UseFilterComponentRefs = () => {
  const operationSelectHandleClickAwayOverride = React.useRef(false);
  const valueSelectHandleClickAwayOverride = React.useRef(false);
  return { handleClickAwayOverrideRefs: { operationSelectHandleClickAwayOverride, valueSelectHandleClickAwayOverride } };
};

const CheckColumnFilter = ({
  filter,
  columnName,
  selectedFilterOperation,
  onFilter,
  addClearFilterStateFn,
  removeClearFilterStateFn,
  messages,
  classNames,
  filterComponentRefs = {}
}: ColumnFilterProps) => {
  const [newFilterValue, setNewFilterValue] = React.useState(String(filter?.value ?? 'all'));

  const onFilterClear = () => {
    setNewFilterValue('all');
    onFilter(null);
    document.body.click();
  };

  const onFilterApply = () => {
    let filterValue;
    if (newFilterValue === 'true') {
      filterValue = true;
    } else if (newFilterValue === 'false') {
      filterValue = false;
    }
    onFilter(!isFilterValueEmpty(filterValue) ? { value: filterValue, operation: selectedFilterOperation } : null);
    document.body.click();
  };

  React.useEffect(() => {
    const clearFilterState = () => setNewFilterValue('all');
    addClearFilterStateFn(columnName, clearFilterState);
    return () => removeClearFilterStateFn(columnName);
  }, [addClearFilterStateFn, removeClearFilterStateFn, columnName, setNewFilterValue]);

  const setValueSelectHandleClickAwayOverride = (override: boolean) => {
    const { handleClickAwayOverrideRefs = {} } = filterComponentRefs;
    if (handleClickAwayOverrideRefs.valueSelectHandleClickAwayOverride) {
      const typedOverrideRef = handleClickAwayOverrideRefs.valueSelectHandleClickAwayOverride as React.MutableRefObject<boolean>;
      typedOverrideRef.current = override;
    }
  };

  const valueSelectProps: SelectProps = {
    options: [
      { name: messages.all, id: 'all' },
      { name: messages.active, id: 'true' },
      { name: messages.inactive, id: 'false' }
    ],
    value: newFilterValue,
    inputLabel: messages.columnFilterLabel,
    onChange: event => setNewFilterValue(event.target.value as string),
    onOpen: () => setValueSelectHandleClickAwayOverride(true),
    onClose: () => setValueSelectHandleClickAwayOverride(false),
    margin: 'dense'
  };

  const clearButtonDisabled = newFilterValue === 'all';
  const applyButtonDisabled = String(filter?.value ?? 'all') === newFilterValue;
  return (
    <>
      <LayoutGrid container justify="space-between" className={classNames.mainContainer}>
        <LayoutGrid item xs={12}>
          <Select {...valueSelectProps} />
        </LayoutGrid>
      </LayoutGrid>
      <LayoutGrid container justify="flex-end">
        <LayoutGrid item>
          <Button color="primary" variant="text" size="small" disabled={clearButtonDisabled} onClick={onFilterClear}>
            {messages.clear}
          </Button>
        </LayoutGrid>
        <LayoutGrid item>
          <Button color="primary" size="small" disabled={applyButtonDisabled} onClick={onFilterApply}>
            {messages.apply}
          </Button>
        </LayoutGrid>
      </LayoutGrid>
    </>
  );
};

export { useCheckColumnFilterRefs, CheckColumnFilter };
